// src/shared/utils/tableSort.ts
//
// Pure, null-safe comparison + sorting for lightweight in-memory Mantine tables.
// Paired with the useTableSort hook and the SortableTh header cell. Column values
// are read through accessor closures, so nested paths (row.totals.cPlays),
// derived values (a title-map lookup) and mixed types all work without special
// support. Sort raw comparable values here — never pre-formatted display strings.

export type SortDirection = 'asc' | 'desc';

/** Accessor returning a comparable primitive for a row; null/undefined sort last. */
export type SortAccessor<T> = (row: T) => string | number | boolean | null | undefined;

type SortValue = string | number | boolean | null | undefined;

export class TableSort {
  /**
   * Null-safe ascending comparison of two primitives. null/undefined always sort
   * last; numbers compare numerically, strings via locale-aware case-insensitive
   * compare, booleans true-first.
   */
  static compareValues(a: SortValue, b: SortValue): number {
    const aNil = a === null || a === undefined;
    const bNil = b === null || b === undefined;
    if (aNil && bNil) return 0;
    if (aNil) return 1;
    if (bNil) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    if (typeof a === 'boolean' && typeof b === 'boolean') return a === b ? 0 : a ? -1 : 1;
    return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
  }

  /**
   * Return a NEW array of `rows` sorted by `accessor` in `dir`. Does not mutate
   * the input. null/undefined values are kept last regardless of direction.
   */
  static sortRows<T>(rows: T[], accessor: SortAccessor<T>, dir: SortDirection): T[] {
    const sign = dir === 'asc' ? 1 : -1;
    return [...rows].sort((ra, rb) => {
      const a = accessor(ra);
      const b = accessor(rb);
      // Keep nils last irrespective of sort direction.
      if (a === null || a === undefined || b === null || b === undefined) {
        return TableSort.compareValues(a, b);
      }
      return sign * TableSort.compareValues(a, b);
    });
  }
}
